import { getDefaultSubscription } from "../../models/data/subscription.data";
import { inject, Injectable } from "@angular/core";
import { DataRepositoryService } from "../DataRepository/data-repository.service";
import { SubscriptionConfigType } from "../../models/types/environement-config.type";
import { Router } from "@angular/router";
import { AppStateStoreService } from "@bitwarden/web-vault/app/services/store/app-state/app-state.store.service";
import { UserStoreService } from "@bitwarden/web-vault/app/services/store/user/user.store.service";
import { ApiService } from "@bitwarden/common/abstractions/api.service";
import { pollUntil } from "@bitwarden/web-vault/app/shared/utils/poll-until";
const subscriptionGeneralEndPoint = process.env.SUBSCRIPTION as unknown as SubscriptionConfigType;

@Injectable({
  providedIn: "root",
})
export class SubscriptionHttpPollingService {
  subscriptionConfig: Record<string, any>;
  private dataRepositoryService = inject(DataRepositoryService);
  private router = inject(Router);
  private appState: AppStateStoreService = inject(AppStateStoreService);
  private userStoreService: UserStoreService = inject(UserStoreService);
  private apiService: ApiService = inject(ApiService);

  constructor() {
    this.subscriptionConfig = subscriptionGeneralEndPoint;
  }

  async pollSubStatus(subscriptionId: string, isFreeTrial: boolean = false): Promise<void> {
    // Do NOT end an already-running "general" process here — a caller (e.g. Bootstrap's
    // "Activating free trial..." step) may already own one, and this method didn't start it.
    // startProcess merges into an existing process instead of replacing it (updating the
    // displayed message, bumping processCount), and our own endProcess below only terminates
    // this method's share of it — terminate() completes the process once every owner has ended
    // its share (processCount === terminatedProcess). This keeps "a flow ends what it started".
    this.appState.startProcess(
      "general",
      isFreeTrial ? "Getting free trial..." : "loading subscription status...",
    );

    try {
      const result = await pollUntil({
        poll: () =>
          subscriptionId
            ? this.verifySubscriptionByTransaction(subscriptionId)
            : Promise.resolve(false),
        isReady: (verified) => verified === true,
        intervalMs: 3000,
        maxAttempts: 5,
        delayBeforeFirstPoll: true,
      });

      if (!result.ready) {
        throw new Error("Subscription verification timed out.");
      }
      await this.apiService.refreshIdentityToken();
      this.userStoreService.subscription.triggerChanges();
    } finally {
      this.appState.endProcess("general");
    }
  }

  async verifySubscriptionByTransaction(subscriptionId: string) {
    try {
      const response = await this.fetchCurActiveSubscriptions();
      return this.verifyPurchaseAsActiveSub(response, subscriptionId);
    } catch (e) {
      return Promise.resolve(getDefaultSubscription());
    }
  }

  async fetchCurActiveSubscriptions() {
    const endpoint = this.subscriptionConfig.endpoint["get_active_subscription"];
    const url = `${this.subscriptionConfig.url}/${this.subscriptionConfig.apiStage}`;
    return await this.dataRepositoryService.send("GET", `${endpoint}`, null, true, true, url);
  }

  verifyPurchaseAsActiveSub(sub: any, subscriptionId: string) {
    return sub.subscription_id === subscriptionId && sub.active === true;
  }
}
